import * as React from 'react'
import { useEffect } from 'react'
import { AppAction, Command, IBackgroundLocationState } from '../config/appConfig'
import { useDispatch, useSelector } from 'react-redux'
import { createLocation, Location } from 'history'
import { Store } from '../reducers'
import { Route, Switch, useHistory, useLocation } from 'react-router-dom'
import SearchFileDialog from '../components/Dialog/SearchFile/SearchFileDialog'
import SearchPartyDialog from '../components/Dialog/SearchParty/SearchPartyDialog'
import EditTimeDialog from '../components/Dialog/EditTime/EditTimeDialog'
import AddTimeDialog from '../components/Dialog/AddTime/AddTimeDialog'
import AddTaskDialog from '../components/Dialog/AddTask/AddTaskDialog'
import AddNoteCommandDialog from '../components/Dialog/AddNote/AddNoteDialog'
import EmailFilingSuggestionDialog from '../components/Dialog/ProcessEmail/EmailFilingSuggestionDialog'

export const COMMAND_PREFIX: string = '/command'

export default function Commands(): React.ReactElement {
    const dispatch = useDispatch()
    const history = useHistory<IBackgroundLocationState>()
    const location = useLocation<IBackgroundLocationState>()
    const command = useSelector<Store, Command | string>(state => state.app.command)

    const getBackground = () => {
        if (location.state?.background) {
            // Inherit the background that we already have.
            return location.state.background
        } else if (history.location && !history.location.pathname.startsWith(COMMAND_PREFIX)) {
            // Go to the previous page, provided it is not a dialog.
            return history.location
        } else {
            // Default to the dashboard.
            return createLocation('/')
        }
    }

    const addBackgroundToCommandDialog = (location: Location<IBackgroundLocationState>): false | void => {
        // Intercept any commands that are triggered by a URL change that don't already carry a background.
        if (!command && location.pathname.startsWith(COMMAND_PREFIX) && !location.state?.background) {
            // Add a background to the URL.
            history.replace(location.pathname, {
                background: getBackground(),
                data: location.state?.data,
            })

            // This prevents that we actually navigate to the new URL without saving the current page state.
            // The history.replace will still allow a navigation event to occur because it will now have a background.
            return false
        }

        // This was not a dialog request that needed a background to be added, so it can just pass.
        return void 0
    }

    // Open the dialog when the path has changed by intercepting all URL changes.
    useEffect(() => {
        // Run once on initialization, because then the history.block() event is not triggered.
        addBackgroundToCommandDialog(location)

        return history.block(addBackgroundToCommandDialog)
    }, [])

    // Open the dialog when a command has been dispatched.
    useEffect(() => {
        if (command && `${COMMAND_PREFIX}/${command}` !== location.pathname) {
            // We were routed through a command context, and need to change the URL to match the command.
            dispatch({ type: AppAction.AcknowledgeCommand })

            // Open the command dialog.
            history.replace(`${COMMAND_PREFIX}/${command}`, {
                background: getBackground(),
            })
        }
    }, [command])

    return (
        <Switch>
            <Route path={`${COMMAND_PREFIX}/${Command.AddNote}`} component={AddNoteCommandDialog} />
            <Route path={`${COMMAND_PREFIX}/${Command.AddTask}`} component={AddTaskDialog} />
            <Route path={`${COMMAND_PREFIX}/${Command.AddTime}`} component={AddTimeDialog} />
            <Route path={`${COMMAND_PREFIX}/${Command.EmailFilingSuggest}`} component={EmailFilingSuggestionDialog} />
            <Route path={`${COMMAND_PREFIX}/time/:id/edit`} component={EditTimeDialog} />
            <Route path={`${COMMAND_PREFIX}/${Command.SearchFile}`} component={SearchFileDialog} />
            <Route path={`${COMMAND_PREFIX}/${Command.SearchParty}`} component={SearchPartyDialog} />
        </Switch>
    )
}
